/**
 * Format currency amounts with M/K abbreviations for better display
 * @param amount - The numeric amount to format
 * @param currency - The currency code (e.g., 'NGN', 'USD', 'GBP')
 * @param locale - The locale for number formatting (default: 'en-US')
 * @returns Formatted currency string with abbreviations
 */
export function formatCurrencyWithAbbreviation(
  amount: number | string,
  currency: string = 'NGN',
  locale: string = 'en-US'
): string {
  // Convert string to number if needed
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle invalid numbers
  if (isNaN(numAmount) || numAmount === 0) {
    return formatCurrency(0, currency, locale);
  }

  // Format based on amount size
  if (numAmount >= 1000000) {
    // Millions
    const millions = numAmount / 1000000;
    const formattedMillions = millions % 1 === 0 
      ? millions.toString() 
      : millions.toFixed(1);
    return `${getCurrencySymbol(currency)}${formattedMillions}M`;
  } else if (numAmount >= 1000) {
    // Thousands
    const thousands = numAmount / 1000;
    const formattedThousands = thousands % 1 === 0 
      ? thousands.toString() 
      : thousands.toFixed(1);
    return `${getCurrencySymbol(currency)}${formattedThousands}K`;
  } else {
    // Less than 1000 - show full amount
    return formatCurrency(numAmount, currency, locale);
  }
}

/**
 * Standard currency formatting without abbreviations
 * @param amount - The numeric amount to format
 * @param currency - The currency code
 * @param locale - The locale for number formatting
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = 'NGN',
  locale: string = 'en-US'
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (error) {
    // Fallback for unsupported currencies
    return `${getCurrencySymbol(currency)}${amount.toLocaleString()}`;
  }
}

/**
 * Get currency symbol for supported currencies
 * @param currency - The currency code
 * @returns Currency symbol string
 */
export function getCurrencySymbol(currency: string): string {
  const symbols: Record<string, string> = {
    'NGN': '₦',
    'USD': '$',
    'GBP': '£',
    'EUR': '€',
    'KES': 'KSh',
    'GHS': 'GH₵',
  };
  
  return symbols[currency.toUpperCase()] || currency.toUpperCase() + ' ';
}

/**
 * Format rent amount specifically for display in reviews
 * Shows abbreviated format for large amounts, full format for smaller amounts
 * @param amount - The rent amount
 * @param currency - The currency code
 * @returns Formatted rent string
 */
export function formatRentAmount(
  amount: number | string,
  currency: string = 'NGN'
): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount) || numAmount === 0) {
    return 'No data';
  }

  // Use abbreviation for amounts >= 100K to save space
  if (numAmount >= 100000) {
    return formatCurrencyWithAbbreviation(numAmount, currency);
  } else {
    return formatCurrency(numAmount, currency);
  }
} 